<template>
  <div>
    <div>
      <label class="fz-12">{{ title }}</label>
      <v-text-field
        :placeholder="`Select ${title.toLowerCase()}`"
        @click="open = true"
        :value="selectedLabel"
        readonly
        dense
        outlined
        hide-details
        :append-icon="value != null ? '' : 'mdi-menu-down'"
      >
        <template #append>
          <v-icon
            v-if="value != null"
            small
            class="cursor-pointer"
            style="font-size: 20px; margin-top: 2px"
            @click.stop="onClear"
            >mdi-close</v-icon
          >
          <v-icon v-else>mdi-menu-down</v-icon>
        </template>
      </v-text-field>
    </div>

    <v-dialog v-model="open" max-width="720" eager>
      <v-card class="axon-dialog">
        <v-card-title class="axon-dialog-header">
          <h2 class="primary--text text-h5 font-weight-bold">{{ title }}</h2>
          <v-spacer />
          <v-btn icon small @click="open = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <div class="px-4 pb-3">
          <v-text-field
            v-model="search"
            placeholder="Search category..."
            dense
            outlined
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
          />
        </div>

        <div v-if="draftLabel" class="axon-selected-container mx-4 mb-3">
          <div>
            <div style="font-size: 16px">
              <strong class="primary--text">{{ draftLabel }}</strong>
            </div>
            <div v-if="draftBreadcrumb" class="axon-selected-breadcrumb">{{ draftBreadcrumb }}</div>
          </div>
          <v-btn small icon color="error" depressed class="text-capitalize" @click="onModalClear">
            <v-icon small style="font-size: 20px">mdi-delete-outline</v-icon>
          </v-btn>
        </div>
        <v-divider class="mb-3" />

        <div v-if="isSearching" class="axon-scroll-area pl-3">
          <v-virtual-scroll
            v-if="filteredFlat.length"
            ref="searchVirtual"
            :key="`search-${open}`"
            :items="filteredFlat"
            :item-height="60"
            class="axon-virtual-fill"
          >
            <template #default="{ item }">
              <div
                class="axon-node-row"
                :class="{ 'axon-node-row--selected': isDraftSelected(item.id) }"
                @click="onDraftSelect(item)"
              >
                <i
                  class="v-icon mdi"
                  :class="
                    isDraftSelected(item.id)
                      ? 'mdi-radiobox-marked axon-icon-selected'
                      : 'mdi-radiobox-blank axon-icon-default'
                  "
                  style="font-size: 18px"
                />
                <span class="axon-node-label">
                  <span class="axon-node-name">{{ item.category_name }}</span>
                  <span class="axon-node-id">({{ item.id }})</span>
                  <span v-if="item.breadcrumb" class="axon-node-breadcrumb">{{ item.breadcrumb }}</span>
                </span>
              </div>
            </template>
          </v-virtual-scroll>
          <div v-if="!filteredFlat.length" class="text-center py-6 grey--text">No results found</div>
        </div>

        <div v-else class="axon-scroll-area pl-3">
          <v-virtual-scroll
            ref="treeVirtual"
            :key="`tree-${open}`"
            :items="visibleNodes"
            :item-height="40"
            class="axon-virtual-fill"
          >
            <template #default="{ item }">
              <div
                class="axon-node-row"
                :class="{ 'axon-node-row--selected': isDraftSelected(item.id) }"
                :style="{ paddingLeft: `${item.depth * 20}px` }"
                @click="onDraftSelect(item)"
              >
                <span v-if="item.hasChildren" class="axon-chevron" @click.stop="onToggle(item.id)">
                  <i
                    class="v-icon mdi axon-chevron-icon"
                    :class="expandedMap[item.id] ? 'mdi-chevron-down' : 'mdi-chevron-right'"
                  />
                </span>
                <span v-else class="axon-chevron-spacer" />
                <i
                  class="v-icon mdi"
                  :class="
                    isDraftSelected(item.id)
                      ? 'mdi-radiobox-marked axon-icon-selected'
                      : 'mdi-radiobox-blank axon-icon-default'
                  "
                  style="font-size: 18px"
                />
                <span class="axon-node-label">
                  <span class="axon-node-name">{{ item.category_name }}</span>
                  <span class="axon-node-id">({{ item.id }})</span>
                </span>
              </div>
            </template>
          </v-virtual-scroll>
        </div>

        <v-divider class="mt-3" />

        <v-card-actions class="axon-dialog-footer px-4 py-3">
          <v-spacer />
          <div>
            <v-btn text class="text-capitalize" @click="open = false">Cancel</v-btn>
            <v-btn color="primary" depressed class="text-capitalize ml-2" @click="onSave"> Save </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axonCategories from '@/assets/json/axon_category.json';
import googleCategories from '@/assets/json/google_taxonomy.json';

const DATA_SOURCES = {
  axon: axonCategories,
  google: googleCategories,
};

function flattenWithBreadcrumb(nodes, ancestors = []) {
  const result = [];
  for (const node of nodes || []) {
    const path = [...ancestors, node.category_name];
    result.push({
      id: node.id,
      category_name: node.category_name,
      breadcrumb: ancestors.length ? ancestors.join(' > ') : '',
    });
    if (Array.isArray(node.children)) {
      result.push(...flattenWithBreadcrumb(node.children, path));
    }
  }
  return result;
}

export default {
  name: 'CategoryPicker',
  props: {
    value: { type: [Number, String, null], default: null },
    title: { type: String, default: 'Axon Category' },
    source: { type: String, default: 'axon' },
  },
  data() {
    const categories = DATA_SOURCES[this.source] || axonCategories;
    const idToNode = Object.create(null);
    const rootIds = [];
    const buildIndex = (nodes, parentId = null) => {
      for (const n of nodes || []) {
        idToNode[n.id] = { ...n, parentId };
        if (parentId == null) rootIds.push(n.id);
        if (Array.isArray(n.children) && n.children.length) buildIndex(n.children, n.id);
      }
    };
    buildIndex(categories);

    return {
      open: false,
      search: '',
      categories,
      expandedMap: {},
      flatList: flattenWithBreadcrumb(categories),
      idToNode,
      rootIds,
      draftId: null,
    };
  },
  computed: {
    selectedLabel() {
      if (this.value == null) return '';
      const found = this.findById(this.categories, this.value);
      return found ? `${found.category_name} (${found.id})` : '';
    },
    draftLabel() {
      if (this.draftId == null) return '';
      const found = this.findById(this.categories, this.draftId);
      return found ? `${found.category_name} (${found.id})` : '';
    },
    draftBreadcrumb() {
      if (this.draftId == null) return '';
      return this.getBreadcrumbById(this.draftId);
    },
    isSearching() {
      return !!(this.search && this.search.trim());
    },
    filteredFlat() {
      if (!this.isSearching) return [];
      const q = this.search.trim().toLowerCase();
      return this.flatList.filter(item => item.category_name.toLowerCase().includes(q) || String(item.id).includes(q));
    },
    visibleNodes() {
      // Build a flat list of only visible nodes based on expandedMap.
      // This keeps DOM small and makes the modal smooth with large JSON.
      const out = [];
      const walk = (nodeId, depth) => {
        const node = this.idToNode[nodeId];
        if (!node) return;
        const hasChildren = Array.isArray(node.children) && node.children.length > 0;
        out.push({
          id: node.id,
          category_name: node.category_name,
          children: node.children,
          depth,
          hasChildren,
        });
        if (hasChildren && this.expandedMap[node.id]) {
          for (const child of node.children) walk(child.id, depth + 1);
        }
      };
      for (const rid of this.rootIds) walk(rid, 0);
      return out;
    },
  },
  methods: {
    isDraftSelected(id) {
      // eslint-disable-next-line eqeqeq
      return this.draftId != null && id == this.draftId;
    },
    onDraftSelect(node) {
      this.draftId = node.id;
    },
    onSave() {
      this.$emit('input', this.draftId);
      this.open = false;
      this.search = '';
    },
    onClear() {
      this.$emit('input', null);
    },
    onModalClear() {
      this.draftId = null;
      this.$emit('input', null);
    },
    onToggle(id) {
      this.$set(this.expandedMap, id, !this.expandedMap[id]);
    },
    expandToId(id) {
      // expand all ancestors so the selected node becomes visible
      // also returns the ancestor chain (root -> ... -> parent)
      const chain = [];
      let cur = this.idToNode[id];
      while (cur && cur.parentId != null) {
        chain.unshift(cur.parentId);
        cur = this.idToNode[cur.parentId];
      }
      for (const ancestorId of chain) {
        this.$set(this.expandedMap, ancestorId, true);
      }
      return chain;
    },
    getBreadcrumbById(id) {
      const names = [];
      let cur = this.idToNode[id];
      while (cur) {
        names.unshift(cur.category_name);
        if (cur.parentId == null) break;
        cur = this.idToNode[cur.parentId];
      }
      // exclude the selected node name itself, keep only parents as breadcrumb context
      if (names.length <= 1) return '';
      names.pop();
      return names.join(' > ');
    },
    scrollToId(id) {
      const index = this.visibleNodes.findIndex(n => n.id === id);
      if (index < 0) return;
      const target = this.$refs.treeVirtual;
      if (target && typeof target.scrollToIndex === 'function') {
        target.scrollToIndex(index);
      }
    },
    findById(list, id) {
      for (const item of list || []) {
        // eslint-disable-next-line eqeqeq
        if (item && item.id == id) return item;
        if (Array.isArray(item?.children) && item.children.length) {
          const found = this.findById(item.children, id);
          if (found) return found;
        }
      }
      return null;
    },

    forceVirtualRecalc() {
      // Vuetify v-virtual-scroll can render blank when its container is inside a freshly-opened dialog
      // (height is 0 for a moment). We nudge it to recalc after a couple paints.
      const runOnce = () => {
        const target = this.isSearching ? this.$refs.searchVirtual : this.$refs.treeVirtual;
        if (target && typeof target.scrollToIndex === 'function') {
          target.scrollToIndex(0);
        }
        window.dispatchEvent(new Event('resize'));
      };

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          runOnce();
          requestAnimationFrame(() => {
            runOnce();
            setTimeout(runOnce, 0);
          });
        });
      });
    },
  },
  watch: {
    open(val) {
      if (!val) {
        this.search = '';
        return;
      }

      // Reset draft to last saved value whenever modal opens
      this.draftId = this.value;
      if (this.value != null) {
        this.expandToId(this.value);
      }
      this.forceVirtualRecalc();

      // after expand, scroll to selected node
      if (this.value != null) {
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            this.scrollToId(this.value);
          });
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.axon-dialog {
  display: flex;
  flex-direction: column;
  height: 80vh;
  max-height: 80vh;
  overflow: hidden;
}

.axon-dialog-header {
  flex: 0 0 auto;
  padding: 12px 16px !important;
}

.axon-dialog-footer {
  flex: 0 0 auto;
}

.axon-scroll-area {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
}

.axon-virtual-fill {
  height: 100% !important;
}

::v-deep .axon-node-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
  user-select: none;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
}

::v-deep .axon-node-row--selected {
  background: rgba(25, 118, 210, 0.08) !important;
}

::v-deep .axon-chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  margin-left: 4px;
  flex: 0 0 24px;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.08);
  }
}

::v-deep .axon-chevron-icon {
  font-size: 20px !important;
  transition: transform 0.15s;
}

::v-deep .axon-chevron-spacer {
  width: 24px;
  flex: 0 0 24px;
}

::v-deep .axon-icon-selected {
  font-size: 18px !important;
  color: var(--v-primary-base, #1976d2) !important;
}

::v-deep .axon-icon-default {
  font-size: 18px !important;
}

::v-deep .axon-node-label {
  flex: 1;
  min-width: 0;
  line-height: 1.4;
}

::v-deep .axon-node-name {
  font-size: 16px;
}

::v-deep .axon-node-id {
  font-size: 16px;
  margin-left: 4px;
}

::v-deep .axon-node-breadcrumb {
  display: block;
  font-size: 14px;
  color: #bdbdbd;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.axon-selected-breadcrumb {
  font-size: 14px;
  color: #9e9e9e;
}

.axon-selected-container {
  font-size: 14px;
  background-color: #f5f5f5;
  padding: 8px 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
